/**
 * Notificações no aparelho e instalação do app.
 * `PushBloco`: rodapé compacto do sino (ativar/desativar). `PushSecao`: seção da página Empresa (tipos, aparelhos).
 * `InstalarDica`: botão "Instalar" (Chrome/Edge) ou a instrução do iPhone; some quando já roda instalado.
 */
import { useQuery } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';
import { dataHoraBR } from '../lib/app';
import { instalar, useInstalar } from '../lib/instalar';
import {
  ativar,
  atualizarTipos,
  desativar,
  explicarMotivo,
  PUSH_TIPOS,
  PUSH_TIPOS_TODOS,
  pushAssinaturasQuery,
  pushChaveQuery,
  PushErro,
  pushEstadoQuery,
  removerAssinatura,
  suporte,
  type PushTipo,
} from '../lib/push';
import { IconBell, IconCheck, IconDoc, IconTrash } from './icons';
import { Skeleton } from './ui';

// ---------- gancho ----------

/** Estado do push neste aparelho + ações; `disponivel` é falso quando o servidor está sem chave (esconde a função). */
function usePush() {
  const chave = useQuery(pushChaveQuery);
  const estado = useQuery(pushEstadoQuery);
  const { ios, instalado } = useInstalar();
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const rodar = async (acao: () => Promise<unknown>) => {
    setOcupado(true);
    setErro(null);
    try {
      await acao();
    } catch (e) {
      setErro(e instanceof PushErro ? explicarMotivo(e.motivo, e.message) : e instanceof Error ? e.message : 'Não foi possível concluir.');
    } finally {
      setOcupado(false);
    }
  };

  return {
    carregando: chave.isPending || estado.isPending,
    disponivel: !!chave.data?.chavePublica,
    suportado: estado.data?.suporte ?? suporte(),
    iosSemInstalar: ios && !instalado && !(estado.data?.suporte ?? false),
    permissao: estado.data?.permissao ?? 'indisponivel',
    ativo: !!estado.data?.endpoint && estado.data.permissao === 'granted',
    endpoint: estado.data?.endpoint ?? null,
    ocupado,
    erro,
    limparErro: () => setErro(null),
    ativar: (tipos?: PushTipo[]) => rodar(() => ativar(tipos)),
    desativar: () => rodar(desativar),
  };
}

function Aviso({ children, tom = 'mute' }: { children: ReactNode; tom?: 'mute' | 'bad' }) {
  return (
    <p role={tom === 'bad' ? 'alert' : 'status'} className={`text-[12px] leading-relaxed ${tom === 'bad' ? 'text-bad' : 'text-mute'}`}>
      {children}
    </p>
  );
}

// ---------- bloco do sino ----------

/** Rodapé do painel de notificações: "Receber no aparelho" com Ativar/Desativar e o estado atual. */
export function PushBloco() {
  const p = usePush();
  const id = useId();
  if (p.carregando) return null;
  if (!p.disponivel) return null;
  if (!p.suportado && !p.iosSemInstalar) return null;

  const estadoTexto = p.ativo
    ? 'Este aparelho recebe as notificações.'
    : p.permissao === 'denied'
      ? 'Bloqueadas pelo navegador.'
      : p.iosSemInstalar
        ? 'No iPhone, adicione à Tela de Início para receber notificações.'
        : 'Receba avisos mesmo com o app fechado.';

  return (
    <div className="border-t border-line px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p id={id} className="text-[13px] font-medium text-fg">
            Receber no aparelho
          </p>
          <p className="text-[12px] text-mute">{estadoTexto}</p>
        </div>
        {!p.iosSemInstalar && (
          <button
            type="button"
            className={`app-btn ${p.ativo ? 'app-btn-ghost' : 'app-btn-primary'} h-8 min-h-0 shrink-0 text-[12px]`}
            aria-describedby={id}
            aria-pressed={p.ativo}
            disabled={p.ocupado}
            onClick={() => void (p.ativo ? p.desativar() : p.ativar())}
          >
            {p.ocupado ? '…' : p.ativo ? 'Desativar' : 'Ativar'}
          </button>
        )}
      </div>
      {p.erro && (
        <div className="mt-2">
          <Aviso tom="bad">{p.erro}</Aviso>
        </div>
      )}
    </div>
  );
}

// ---------- seção da página Empresa ----------

/** "Notificações no aparelho": liga/desliga, tipos por aparelho e a lista de aparelhos da pessoa. */
export function PushSecao() {
  const p = usePush();
  const lista = useQuery({ ...pushAssinaturasQuery, enabled: p.disponivel });
  const tituloId = useId();
  const [salvandoTipos, setSalvandoTipos] = useState(false);
  const [erroLista, setErroLista] = useState<string | null>(null);
  const [removendo, setRemovendo] = useState<string | null>(null);

  if (p.carregando) return <Skeleton h={160} />;
  if (!p.disponivel) return null;

  const este = lista.data?.itens.find((a) => a.endpoint === p.endpoint) ?? null;
  const tipos = este?.tipos ?? PUSH_TIPOS_TODOS;

  const trocarTipo = async (tipo: PushTipo, on: boolean) => {
    const novos = on ? [...new Set([...tipos, tipo])] : tipos.filter((t) => t !== tipo);
    setSalvandoTipos(true);
    setErroLista(null);
    try {
      await atualizarTipos(novos);
    } catch (e) {
      setErroLista(e instanceof Error ? e.message : 'Não foi possível salvar os tipos.');
    } finally {
      setSalvandoTipos(false);
    }
  };

  const remover = async (endpoint: string) => {
    setRemovendo(endpoint);
    setErroLista(null);
    try {
      if (endpoint === p.endpoint) await p.desativar();
      else await removerAssinatura(endpoint);
    } catch (e) {
      setErroLista(e instanceof Error ? e.message : 'Não foi possível remover o aparelho.');
    } finally {
      setRemovendo(null);
    }
  };

  return (
    <section aria-labelledby={tituloId} className="app-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id={tituloId} className="flex items-center gap-2 text-[15px] font-semibold">
            <IconBell size={15} /> Notificações no aparelho
          </h2>
          <p className="mt-0.5 text-[12px] text-mute">
            Avisos no computador ou no celular mesmo com o app fechado. Cada aparelho é ativado nele mesmo; o e-mail dos contatos continua.
          </p>
        </div>
        {p.suportado && (
          <button
            type="button"
            className={`app-btn ${p.ativo ? 'app-btn-ghost' : 'app-btn-primary'} shrink-0`}
            aria-pressed={p.ativo}
            disabled={p.ocupado}
            onClick={() => void (p.ativo ? p.desativar() : p.ativar())}
          >
            {p.ocupado ? 'aguarde…' : p.ativo ? 'Desativar neste aparelho' : 'Ativar neste aparelho'}
          </button>
        )}
      </div>

      <div className="mt-3 flex flex-col gap-2">
        {p.erro && <Aviso tom="bad">{p.erro}</Aviso>}
        {!p.erro && p.iosSemInstalar && <Aviso>{explicarMotivo('ios-nao-instalado', '')}</Aviso>}
        {!p.erro && !p.suportado && !p.iosSemInstalar && <Aviso>{explicarMotivo('sem-suporte', '')}</Aviso>}
        {!p.erro && p.suportado && !p.ativo && p.permissao === 'denied' && <Aviso>{explicarMotivo('negado', '')}</Aviso>}
        {!p.erro && p.suportado && p.ativo && (
          <Aviso>
            <span className="inline-flex items-center gap-1 text-ok">
              <IconCheck size={12} /> Este aparelho recebe as notificações.
            </span>
          </Aviso>
        )}
      </div>

      {p.ativo && (
        <fieldset className="mt-4" disabled={salvandoTipos || p.ocupado}>
          <legend className="app-lbl mb-1.5">O que avisar neste aparelho</legend>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {PUSH_TIPOS.map((t) => {
              const on = tipos.includes(t.id);
              return (
                <label
                  key={t.id}
                  className="flex cursor-pointer items-start gap-2 rounded-lg border border-line px-2.5 py-2 text-[13px] hover:bg-bg-3"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 accent-accent"
                    checked={on}
                    onChange={(e) => void trocarTipo(t.id, e.target.checked)}
                  />
                  <span className="flex flex-col">
                    <span className="font-medium text-fg">{t.label}</span>
                    <span className="text-[12px] text-mute">{t.descricao}</span>
                  </span>
                </label>
              );
            })}
          </div>
          {tipos.length === 0 && <Aviso>Nenhum tipo marcado: este aparelho não recebe avisos até marcar algum.</Aviso>}
        </fieldset>
      )}

      <div className="mt-4">
        <h3 className="app-lbl mb-1.5">Aparelhos que recebem</h3>
        {lista.isPending ? (
          <Skeleton h={44} />
        ) : lista.isError ? (
          <Aviso tom="bad">{lista.error.message}</Aviso>
        ) : lista.data.itens.length === 0 ? (
          <Aviso>Nenhum aparelho ativado ainda.</Aviso>
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line">
            {lista.data.itens.map((a) => {
              const esteAparelho = a.endpoint === p.endpoint;
              return (
                <li key={a.id} className="flex items-center gap-3 px-3 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-1.5 text-[13px] text-fg">
                      {a.aparelho || 'Aparelho'}
                      {esteAparelho && <span className="app-chip app-chip-accent">este aparelho</span>}
                    </span>
                    <span className="block text-[11px] text-mute">
                      Ativado em {dataHoraBR(a.criadoEm)}
                      {a.ultimoEnvioEm ? ` · último aviso ${dataHoraBR(a.ultimoEnvioEm)}` : ''}
                      {a.tipos.length < PUSH_TIPOS_TODOS.length ? ` · ${a.tipos.length} de ${PUSH_TIPOS_TODOS.length} tipos` : ''}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="app-btn app-btn-ghost h-8 min-h-0 px-2 text-[12px] text-bad"
                    aria-label={`Remover ${a.aparelho || 'aparelho'}`}
                    disabled={removendo !== null || p.ocupado}
                    onClick={() => void remover(a.endpoint)}
                  >
                    <IconTrash size={13} /> {removendo === a.endpoint ? '…' : 'Remover'}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {erroLista && (
          <div className="mt-2">
            <Aviso tom="bad">{erroLista}</Aviso>
          </div>
        )}
      </div>

      <InstalarDica className="mt-4 border-t border-line pt-4" />
    </section>
  );
}

// ---------- instalar ----------

/**
 * Convite para instalar o app: botão nativo (Chrome/Edge) ou a instrução do iPhone. Nada quando já roda instalado
 * ou quando o navegador não oferece convite (Firefox, Safari no Mac).
 */
export function InstalarDica({ className = '', compacta = false }: { className?: string; compacta?: boolean }) {
  const { convite, instalado, ios } = useInstalar();
  const [aceito, setAceito] = useState(false);
  if (instalado || aceito) return null;
  if (!convite && !ios) return null;
  return (
    <div className={className}>
      <p className={`flex items-center gap-1.5 font-semibold text-fg ${compacta ? 'text-[12px]' : 'text-[13px]'}`}>
        <IconDoc size={13} /> Instalar o app
      </p>
      {convite ? (
        <>
          <p className={`mt-0.5 text-mute ${compacta ? 'text-[12px]' : 'text-[12px]'}`}>
            Ícone próprio, janela própria e notificações. É o mesmo app, sem loja.
          </p>
          <button
            type="button"
            className="app-btn app-btn-ghost mt-2 h-8 min-h-0 text-[12px]"
            onClick={() => void instalar().then((ok) => setAceito(ok))}
          >
            Instalar
          </button>
        </>
      ) : (
        <p className="mt-0.5 text-[12px] leading-relaxed text-mute">
          No iPhone ou iPad: toque em Compartilhar e depois em “Adicionar à Tela de Início”.
        </p>
      )}
    </div>
  );
}
